import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

interface KpiCardProps {
  label: string;
  value: string | number;
  delta?: { value: number; label?: string; direction?: "up-good" | "down-good" };
  hint?: string;
  icon?: React.ReactNode;
  accent?: "default" | "danger" | "warn" | "info" | "success";
  className?: string;
  children?: React.ReactNode;
}

const accentStyles: Record<NonNullable<KpiCardProps["accent"]>, string> = {
  default: "",
  danger: "before:bg-rose-500/40",
  warn: "before:bg-amber-500/40",
  info: "before:bg-sky-500/40",
  success: "before:bg-emerald-500/40",
};

export function KpiCard({ label, value, delta, hint, icon, accent = "default", className, children }: KpiCardProps) {
  const dir = delta?.direction ?? "up-good";
  const isPositive = (delta?.value ?? 0) > 0;
  const isNegative = (delta?.value ?? 0) < 0;
  const positiveIsGood = dir === "up-good" ? isPositive : isNegative;
  const negativeIsBad = dir === "up-good" ? isNegative : isPositive;
  const deltaTone = positiveIsGood
    ? "text-emerald-400"
    : negativeIsBad
      ? "text-rose-400"
      : "text-muted-foreground";
  return (
    <Card
      className={cn(
        "relative overflow-hidden p-4",
        "before:absolute before:left-0 before:top-0 before:h-full before:w-px before:opacity-60",
        accentStyles[accent],
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
        {icon && <span className="text-muted-foreground/70">{icon}</span>}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {delta && (
          <div className={cn("inline-flex items-center gap-0.5 text-xs", deltaTone)}>
            {delta.value > 0 ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : delta.value < 0 ? (
              <ArrowDownRight className="h-3 w-3" />
            ) : (
              <Minus className="h-3 w-3" />
            )}
            <span className="tabular-nums">{Math.abs(delta.value)}{delta.label ? ` ${delta.label}` : ""}</span>
          </div>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      {children && <div className="mt-3">{children}</div>}
    </Card>
  );
}
